import React, { useState } from "react";

import { DestinationTypeEnum } from "../../../../enums/destination";

import { FormikErrors } from "formik";
import Button from "../../../../components/form/Button";
import Input from "../../../../components/form/Input";
import { ShopifyDestination } from "../../../../models";
import { mask } from "../../utils/formValidationUtils";
import { StatusSelector } from "../common/StatusSelector";
import ShopifyBlogIdGuideModal from "../modals/ShopifyBlogIdGuideModal";
import ShopifyGuideModal from "../modals/ShopifyGuideModal";

interface ShopifyProps {
  values: ShopifyDestination;
  errors: FormikErrors<ShopifyDestination>;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

const Shopify: React.FC<ShopifyProps> = ({ values, errors, setFieldValue }) => {
  const [showAccessTokenGuide, setShowAccessTokenGuide] = useState(false);
  const [showBlogIdGuide, setShowBlogIdGuide] = useState(false);

  return (
    <>
      <div className="py-4 px-4 sm:px-6 flex flex-col gap-6 mb-4">
        <div className="flex flex-col md:flex-row gap-6">
          <Input
            id="name"
            label="Destination Name"
            name="name"
            type="text"
            required
            placeholder="Name your destination"
            className="max-w-md"
            tooltip="Name your destination (e.g., My Shopify Site)"
            value={values.name}
            errorMessage={errors?.name}
            errorPosition="bottom-left"
            onChange={(e: any) => setFieldValue("name", e.target.value)}
          />

          <Input
            id="storeName"
            label="Store Name"
            name="destination.storeName"
            type="text"
            required
            placeholder="Name of your Shopify store (e.g. 6zabnw-ea)"
            className="max-w-md"
            tooltip='The Shopify store name (e.g. in https://admin.shopify.com/store/6zabnw-ea/content, the store name is "6zabnw-ea").'
            value={values.destination.storeName}
            errorMessage={errors?.destination?.storeName}
            errorPosition="bottom-left"
            onChange={(e: any) =>
              setFieldValue("destination.storeName", e.target.value)
            }
          />
        </div>
        <div className="flex flex-col md:flex-row gap-10 md:gap-5 mb-4">
          <div className="relative w-full">
            <Input
              id="blogId"
              label="Blog ID"
              name="blogId"
              type="text"
              required
              placeholder="Blog ID"
              className="max-w-md"
              tooltip="The numeric ID of your Shopify blog where articles will be published."
              value={values.destination.blogId}
              errorMessage={errors?.destination?.blogId}
              errorPosition="bottom-left"
              fontMono
              onChange={(e: any) => {
                setFieldValue("destination.blogId", e.target.value);
              }}
            />
            <Button
              paddingZero
              type="button"
              variant="transparent-primary"
              className="absolute mt-1"
              onClick={() => setShowBlogIdGuide(true)}
            >
              How to get your blog ID
            </Button>
          </div>
          <div className="relative w-full">
            <Input
              id="secretKey"
              label="Access Token"
              name="secretKey"
              type="text"
              required
              placeholder="Access Token"
              className="max-w-md"
              tooltip="Shopify Admin API access token."
              value={values.destination.secretKey}
              errorMessage={errors?.destination?.secretKey}
              errorPosition="bottom-left"
              fontMono
              onChange={(e: any) => {
                setFieldValue("destination.secretKey", e.target.value);
              }}
              onFocus={(e: any) => {
                if (e.target.value.includes(mask)) {
                  setFieldValue("destination.secretKey", "");
                }
              }}
            />
            <Button
              paddingZero
              type="button"
              variant="transparent-primary"
              className="absolute mt-1"
              onClick={() => setShowAccessTokenGuide(true)}
            >
              How to generate
            </Button>
          </div>
        </div>
        <StatusSelector
          status={values?.destination.status}
          onChange={(status: string) =>
            setFieldValue("destination.status", status)
          }
          platform={DestinationTypeEnum.SHOPIFY}
        />
        <Input
          id="author"
          label="Author Name"
          name="destination.author"
          type="text"
          tooltip="Specify the Shopify author name to attribute to articles."
          placeholder="Optional. Shopify Author Name. If blank, it will be set as 'Admin'"
          className="max-w-md"
          value={values?.destination?.author ?? ""}
          errorMessage={errors?.destination?.author}
          errorPosition="bottom-left"
          onChange={(e: any) => {
            setFieldValue("destination.author", e.target.value);
          }}
          onKeyDown={(e: any) => {
            if (/\d/.test(e.key)) {
              e.preventDefault();
            }
          }}
        />
      </div>

      {showAccessTokenGuide && (
        <ShopifyGuideModal
          onRequestClose={() => setShowAccessTokenGuide(false)}
        />
      )}
      {showBlogIdGuide && (
        <ShopifyBlogIdGuideModal
          onRequestClose={() => setShowBlogIdGuide(false)}
        />
      )}
    </>
  );
};

export default Shopify;
